import { useState } from "react";
import { NavLink, useNavigate, useLocation, useMatch } from "react-router-dom";
import CIcon from "@coreui/icons-react";
import { cilSpeedometer, cilCalendar, cilChart, cilPeople, cilList, cilSettings, cilNotes, cilColumns, cilBell, cilUser, cilGraph, cilHistory, cilClock, cilSync, cilHeart, cilLoopCircular, cilTransfer, cilChartLine, cilTask } from "@coreui/icons";
import { getUser, logout } from "../api/auth.api";
import { useAlertaCount } from "../utils/tarefasAlerta";
import NotificacoesBell from "../components/NotificacoesBell";

/* ─── Dados ───────────────────────────────────────────────────────── */

const ACESSO_RAPIDO = [
  { to: "/developers/resumo",       icon: cilPeople,        label: "Devs — Resumo"            },
  { to: "/developers/kanban",       icon: cilColumns,       label: "Devs — Kanban"             },
  { to: "/projetos/acompanhamento", icon: cilGraph,         label: "Projetos — Acompanhamento" },
  { to: "/tarefas/acompanhamento",  icon: cilNotes,         label: "Tarefas — Acompanhamento"  },
  { to: "/tarefas/calendario",      icon: cilCalendar,      label: "Tarefas — Calendário"      },
  { to: "/tickets/ciclo",           icon: cilLoopCircular,  label: "Tickets — Ciclo de Vida"   },
];

const LINKS = [
  { to: "/",         icon: cilSpeedometer, label: "Dashboard", exact: true },
  { to: "/gtd",      icon: cilTask,        label: "Quadro GTD", exact: true },
  { to: "/sprint",   icon: cilCalendar,    label: "Sprint", exact: true     },
  { to: "/burndown",         icon: cilChart,  label: "Burndown"   },
  { to: "/sprint/historico",      icon: cilGraph,   label: "Histórico"      },
  { to: "/sprint/retrospectiva",  icon: cilHistory, label: "Retrospectiva"  },
  { to: "/sprint/estimativas",   icon: cilClock,   label: "Estimativas"     },
  { to: "/sprint/saude",         icon: cilHeart,   label: "Saúde da Sprint" },
  // { to: "/sprint/comparar", icon: cilSwapHorizontal, label: "Comparar" }, // oculto temporariamente
];

const GROUPS = [
  {
    key: "developers",
    label: "Desenvolvedores",
    items: [
      { to: "/developers/resumo",       icon: cilPeople,       label: "Resumo"       },
      { to: "/developers/kanban",       icon: cilColumns,      label: "Kanban"       },
      { to: "/developers/retrabalhos",  icon: cilSync,         label: "Retrabalhos"  },
      { to: "/developers/velocidade",   icon: cilGraph,        label: "Velocidade"   },
      { to: "/developers/produtividade", icon: cilChartLine,   label: "Produtividade" },
      { to: "/developers/tempos",       icon: cilClock,        label: "Tempos"        },
    ],
  },
  {
    key: "programacao",
    label: "Programação",
    items: [
      { to: "/programacao/setup",          icon: cilSettings, label: "Setup"          },
      { to: "/programacao/kanban",         icon: cilList,     label: "Kanban"         },
      { to: "/programacao/gargalos",       icon: cilChart,    label: "Gargalos"       },
      { to: "/programacao/transferencias", icon: cilTransfer, label: "Transferências" },
      { to: "/programacao/emergencia",     icon: cilBell,     label: "Emergência"     },
    ],
  },
  {
    key: "projetos",
    label: "Projetos",
    items: [
      { to: "/projetos/dashboard",      icon: cilSpeedometer, label: "Dashboard"      },
      { to: "/projetos/acompanhamento", icon: cilNotes,       label: "Acompanhamento" },
    ],
  },
  {
    key: "tarefas",
    label: "Tarefas",
    items: [
      { to: "/tarefas/dashboard",      icon: cilSpeedometer, label: "Dashboard"      },
      { to: "/tarefas/setup",          icon: cilSettings,    label: "Setup"          },
      { to: "/tarefas/acompanhamento", icon: cilNotes,       label: "Acompanhamento" },
      { to: "/tarefas/prazos",         icon: cilBell,        label: "Prazos"         },
      { to: "/tarefas/calendario",     icon: cilCalendar,    label: "Calendário"     },
      { to: "/tarefas/responsaveis",   icon: cilUser,        label: "Responsáveis"   },
    ],
  },
  {
    key: "tickets",
    label: "Tickets",
    items: [
      { to: "/tickets/ciclo", icon: cilLoopCircular, label: "Ciclo de Vida" },
    ],
  },
];

function loadOpenGroups(location) {
  try {
    const saved = localStorage.getItem("sidebar_groups");
    if (saved) return JSON.parse(saved);
  } catch {}
  // padrão: abre só o grupo da rota atual
  const defaults = { developers: false, programacao: false, projetos: false, tarefas: false, tickets: false };
  GROUPS.forEach(g => {
    if (g.items.some(i => location.pathname.startsWith(i.to.split("/").slice(0, 2).join("/")))) {
      defaults[g.key] = true;
    }
  });
  return defaults;
}

/* ─── Componente ──────────────────────────────────────────────────── */

export default function Sidebar({ collapsed, onToggle, onBusca }) {
  const navigate   = useNavigate();
  const location   = useLocation();
  const userData   = getUser();
  const userName   = userData?.name  || userData?.login || "Usuário";
  const userLogin  = userData?.login || "";
  const isSettings = !!useMatch("/parametros");

  const [openGroups, setOpenGroups] = useState(() => loadOpenGroups(location));
  const [hoverGroup, setHoverGroup] = useState(null);
  const [quickOpen,  setQuickOpen]  = useState(() => {
    try { return JSON.parse(localStorage.getItem("sidebar_quick") ?? "false"); } catch { return false; }
  });
  const alertaCount = useAlertaCount();

  function toggleQuick() {
    setQuickOpen(prev => {
      const next = !prev;
      localStorage.setItem("sidebar_quick", JSON.stringify(next));
      return next;
    });
  }

  function toggleGroup(key) {
    setOpenGroups(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem("sidebar_groups", JSON.stringify(next));
      return next;
    });
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <aside style={{ ...sidebar, width: collapsed ? 64 : 220 }}>

      {/* ── Brand ── */}
      <div style={{ ...brand, justifyContent: collapsed ? "center" : "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, overflow: "hidden" }}>
          <div style={logo}>R</div>
          {!collapsed && (
            <div style={{ overflow: "hidden" }}>
              <div style={brandName}>Produção</div>
              <div style={brandSub}>Sprint Board</div>
            </div>
          )}
        </div>
        {!collapsed && (
          <button
            onClick={() => navigate("/parametros")}
            title="Configurações"
            style={{ ...settingsBtn, ...(isSettings ? settingsBtnActive : {}) }}
          >
            <CIcon icon={cilSettings} style={{ width: 15, height: 15 }} />
          </button>
        )}
      </div>

      {/* ── Busca ── */}
      <button
        style={{ ...searchBtn, justifyContent: collapsed ? "center" : "flex-start" }}
        onClick={onBusca}
        title="Busca global (Ctrl+K)"
      >
        <span style={{ fontSize: 14 }}>🔍</span>
        {!collapsed && (
          <>
            <span style={{ fontSize: 12, color: "#334155", flex: 1, textAlign: "left" }}>Buscar...</span>
            <kbd style={searchKbd}>Ctrl K</kbd>
          </>
        )}
      </button>

      {/* ── Nav ── */}
      <nav style={nav}>

        {/* Acesso Rápido */}
        {!collapsed ? (
          <button
            onClick={toggleQuick}
            style={{
              ...groupHeader,
              background: quickOpen ? "rgba(251,191,36,0.06)" : "transparent",
            }}
          >
            <div style={quickHeader}>
              <span style={{ fontSize: 10, lineHeight: 1 }}>★</span>
              <span>Acesso Rápido</span>
            </div>
            <span style={{ fontSize: 11, color: "#fbbf24", opacity: 0.7, lineHeight: 1 }}>
              {quickOpen ? "▼" : "▶"}
            </span>
          </button>
        ) : (
          <div
            onClick={toggleQuick}
            title={quickOpen ? "Ocultar acesso rápido" : "Mostrar acesso rápido"}
            style={{ borderTop: "1px solid rgba(251,191,36,0.2)", margin: "4px 2px 6px", cursor: "pointer" }}
          />
        )}

        {quickOpen && ACESSO_RAPIDO.map(({ to, icon, label }) => (
          <NavLink
            key={to}
            to={to}
            title={label}
            style={({ isActive }) => ({
              ...link,
              ...quickLink,
              justifyContent: collapsed ? "center" : "flex-start",
              ...(isActive ? quickLinkActive : {}),
            })}
          >
            {({ isActive }) => (
              <>
                <span style={{ ...iconWrap, ...(isActive ? quickIconActive : quickIcon) }}>
                  <CIcon icon={icon} style={{ width: 14, height: 14 }} />
                </span>
                {!collapsed && <span style={{ ...linkLabel, fontSize: 12 }}>{label}</span>}
                {!collapsed && isActive && <span style={{ ...activeDot, background: "#fbbf24", boxShadow: "0 0 6px #fbbf24" }} />}
              </>
            )}
          </NavLink>
        ))}

        {/* Separador */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)", margin: "6px 2px 4px" }} />

        {/* Links fixos */}
        {LINKS.map(({ to, icon, label, exact }) => (
          <NavLink
            key={to}
            to={to}
            end={exact}
            title={collapsed ? label : undefined}
            style={({ isActive }) => ({
              ...link,
              justifyContent: collapsed ? "center" : "flex-start",
              ...(isActive ? linkActive : {}),
            })}
          >
            {({ isActive }) => (
              <>
                <span style={{ ...iconWrap, ...(isActive ? iconWrapActive : {}) }}>
                  <CIcon icon={icon} style={{ width: 16, height: 16 }} />
                </span>
                {!collapsed && <span style={linkLabel}>{label}</span>}
                {!collapsed && isActive && <span style={activeDot} />}
              </>
            )}
          </NavLink>
        ))}

        {/* Grupos */}
        {GROUPS.map(({ key, label, items }) => {
          const isOpen    = collapsed || openGroups[key];
          const hasActive = items.some(i => location.pathname.startsWith("/" + i.to.split("/")[1]));

          return (
            <div key={key} style={{ marginTop: 4 }}>

              {/* Cabeçalho do grupo */}
              {!collapsed && (
                <button
                  style={{
                    ...groupHeader,
                    background: hoverGroup === key
                      ? "rgba(96,165,250,0.07)"
                      : hasActive
                        ? "rgba(96,165,250,0.04)"
                        : "transparent",
                  }}
                  onClick={() => toggleGroup(key)}
                  onMouseEnter={() => setHoverGroup(key)}
                  onMouseLeave={() => setHoverGroup(null)}
                >
                  <span style={{ ...groupLabel, color: hasActive ? "#60a5fa" : "#475569" }}>
                    {label}
                  </span>
                  <span style={{ fontSize: 11, color: hasActive ? "#60a5fa" : "#475569", lineHeight: 1 }}>
                    {openGroups[key] ? "▼" : "▶"}
                  </span>
                </button>
              )}

              {collapsed && <div style={{ height: 4 }} />}

              {/* Itens */}
              {isOpen && items.map(({ to, icon, label: itemLabel }) => {
                const isPrazos = to === "/tarefas/prazos";
                const badge = isPrazos && alertaCount > 0 ? alertaCount : null;
                return (
                  <NavLink
                    key={to}
                    to={to}
                    title={collapsed ? itemLabel : undefined}
                    style={({ isActive }) => ({
                      ...link,
                      ...(!collapsed ? { paddingLeft: 16 } : { justifyContent: "center" }),
                      ...(isActive ? linkActive : {}),
                    })}
                  >
                    {({ isActive }) => (
                      <>
                        <span style={{ ...iconWrap, ...(isActive ? iconWrapActive : {}) }}>
                          <CIcon icon={icon} style={{ width: 14, height: 14 }} />
                        </span>
                        {!collapsed && <span style={{ ...linkLabel, fontSize: 12 }}>{itemLabel}</span>}
                        {!collapsed && isActive && <span style={activeDot} />}
                        {badge && (
                          <span style={alertaBadge}>{badge}</span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}

            </div>
          );
        })}

      </nav>

      {/* ── Notificações ── */}
      <NotificacoesBell collapsed={collapsed} />

      {/* ── Toggle ── */}
      <button onClick={onToggle} title={collapsed ? "Expandir menu" : "Recolher menu"} style={toggleBtn}>
        <span style={{ fontSize: 14, lineHeight: 1 }}>{collapsed ? "›" : "‹"}</span>
        {!collapsed && <span style={{ fontSize: 11 }}>Recolher</span>}
      </button>

      {/* ── Usuário + Logout ── */}
      <div style={{ ...userRow, justifyContent: collapsed ? "center" : "space-between" }}>
        {!collapsed ? (
          <>
            <div style={{ minWidth: 0 }}>
              <div style={userNameStyle} title={userName}>{userName}</div>
              <div style={userLoginStyle}>{userLogin}</div>
            </div>
            <button
              onClick={handleLogout}
              title="Sair"
              style={logoutBtn}
              onMouseEnter={e => { e.currentTarget.style.color = "#f87171"; e.currentTarget.style.borderColor = "rgba(248,113,113,0.3)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "#475569"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.1)"; }}
            >⏻</button>
          </>
        ) : (
          <button
            onClick={handleLogout}
            title="Sair"
            style={{ ...logoutBtn, width: 30, height: 30 }}
            onMouseEnter={e => { e.currentTarget.style.color = "#f87171"; e.currentTarget.style.borderColor = "rgba(248,113,113,0.3)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "#475569"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.1)"; }}
          >⏻</button>
        )}
      </div>

    </aside>
  );
}

/* ─── Estilos ─────────────────────────────────────────────────────── */

const sidebar = {
  height: "100vh",
  background: "linear-gradient(180deg, #08122a 0%, #060d1e 100%)",
  borderRight: "1px solid rgba(96,165,250,0.08)",
  display: "flex",
  flexDirection: "column",
  padding: "20px 10px",
  gap: 2,
  transition: "width 0.22s ease",
  overflow: "hidden",
  flexShrink: 0,
};

const brand = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "0 4px 16px",
  borderBottom: "1px solid rgba(96,165,250,0.08)",
  marginBottom: 8,
  overflow: "hidden",
};

const logo = {
  width: 36,
  height: 36,
  borderRadius: 10,
  background: "linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800,
  fontSize: 17,
  color: "#fff",
  boxShadow: "0 0 16px rgba(6,182,212,0.45)",
  flexShrink: 0,
};

const brandName = {
  color: "#e2e8f0",
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: 0.3,
  whiteSpace: "nowrap",
};

const brandSub = {
  color: "#475569",
  fontSize: 10,
  marginTop: 1,
  letterSpacing: 0.5,
  textTransform: "uppercase",
  whiteSpace: "nowrap",
};

const nav = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  paddingRight: 2,
};

const link = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 10px",
  borderRadius: 10,
  color: "#475569",
  textDecoration: "none",
  fontSize: 13,
  fontWeight: 500,
  position: "relative",
  transition: "color 0.15s",
  whiteSpace: "nowrap",
};

const linkActive = {
  background: "linear-gradient(90deg, rgba(59,130,246,0.18) 0%, rgba(6,182,212,0.08) 100%)",
  color: "#60a5fa",
  boxShadow: "inset 0 0 0 1px rgba(59,130,246,0.22)",
};

const iconWrap = {
  width: 28,
  height: 28,
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(255,255,255,0.04)",
  flexShrink: 0,
};

const iconWrapActive = {
  background: "rgba(59,130,246,0.2)",
  boxShadow: "0 0 8px rgba(59,130,246,0.3)",
};

const linkLabel = { flex: 1, overflow: "hidden" };

const activeDot = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: "#60a5fa",
  boxShadow: "0 0 6px #60a5fa",
  flexShrink: 0,
};

const groupHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: "5px 10px",
  border: "none",
  borderRadius: 7,
  cursor: "pointer",
  transition: "background 0.12s",
};

const groupLabel = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.8,
  whiteSpace: "nowrap",
};

const toggleBtn = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  width: "100%",
  padding: "8px 10px",
  borderRadius: 10,
  border: "1px solid rgba(96,165,250,0.12)",
  background: "rgba(96,165,250,0.05)",
  color: "#475569",
  cursor: "pointer",
  marginTop: 6,
  transition: "all 0.15s",
  flexShrink: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
};

const userRow = {
  borderTop: "1px solid rgba(96,165,250,0.08)",
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 4px 0",
  marginTop: 4,
  overflow: "hidden",
};

const userNameStyle = {
  color: "#94a3b8",
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const userLoginStyle = {
  color: "#334155",
  fontSize: 10,
  marginTop: 1,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const searchBtn = {
  display: "flex", alignItems: "center", gap: 8,
  padding: "7px 10px", borderRadius: 9,
  border: "1px solid rgba(96,165,250,0.12)",
  background: "rgba(96,165,250,0.04)",
  color: "#475569", cursor: "pointer",
  marginBottom: 6, width: "100%",
  transition: "background 0.15s",
};

const searchKbd = {
  fontSize: 9, color: "#334155",
  background: "rgba(96,165,250,0.08)",
  border: "1px solid rgba(96,165,250,0.15)",
  borderRadius: 4, padding: "1px 4px",
  fontFamily: "monospace",
};

const alertaBadge = {
  fontSize: 9,
  fontWeight: 800,
  color: "#fff",
  background: "#f87171",
  borderRadius: 8,
  padding: "1px 5px",
  marginLeft: 2,
  flexShrink: 0,
  lineHeight: 1.6,
};

const logoutBtn = {
  width: 28,
  height: 28,
  borderRadius: 7,
  border: "1px solid rgba(96,165,250,0.1)",
  background: "transparent",
  color: "#475569",
  fontSize: 14,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "color 0.15s, border-color 0.15s",
  flexShrink: 0,
  lineHeight: 1,
};

const settingsBtn = {
  width: 28, height: 28, borderRadius: 7, flexShrink: 0,
  border: "1px solid rgba(96,165,250,0.1)", background: "transparent",
  color: "#475569", cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  transition: "color 0.15s, border-color 0.15s, background 0.15s",
};

const settingsBtnActive = {
  color: "#60a5fa",
  background: "rgba(96,165,250,0.12)",
  borderColor: "rgba(96,165,250,0.35)",
};

/* ── Acesso Rápido ── */
const quickHeader = {
  display: "flex",
  alignItems: "center",
  gap: 5,
  fontSize: 9,
  fontWeight: 800,
  color: "#fbbf24",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  opacity: 0.8,
};

const quickLink = {
  padding: "6px 10px",
  borderRadius: 8,
};

const quickLinkActive = {
  background: "linear-gradient(90deg, rgba(251,191,36,0.14) 0%, rgba(251,191,36,0.04) 100%)",
  color: "#fbbf24",
  boxShadow: "inset 0 0 0 1px rgba(251,191,36,0.2)",
};

const quickIcon = {
  background: "rgba(251,191,36,0.06)",
};

const quickIconActive = {
  background: "rgba(251,191,36,0.18)",
  boxShadow: "0 0 8px rgba(251,191,36,0.25)",
};
